import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Loader, StatusBadge, fmtDateTime, fmtDate, EmptyState } from "@/components/shared";
import { AdminPageHeader, ApproveButton, RejectButton, ImageViewer } from "./adminShared";

const filters = ["all", "pending", "approved", "rejected"];

export default function AdminVerification() {
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("pending");

  const load = useCallback(async () => {
    setItems(null);
    const r = await api.get("/admin/verification", { params: { status } });
    setItems(r.data);
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => { try { await api.post(`/admin/verification/${id}/approve`); toast.success("KYC approved"); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const reject = async (id, reason) => { try { await api.post(`/admin/verification/${id}/reject`, { reason }); toast.success("KYC rejected"); load(); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <div data-testid="admin-verification">
      <AdminPageHeader eyebrow="Management" title="KYC Verification"
        right={<div className="flex gap-2 flex-wrap">{filters.map((f) => (
          <button key={f} data-testid={`kyc-filter-${f}`} onClick={() => setStatus(f)}
            className={`px-4 py-2 rounded-lg text-sm border capitalize transition-colors ${status === f ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-300" : "border-white/10 text-slate-400 hover:border-cyan-500/20"}`}>{f}</button>
        ))}</div>} />
      {items === null ? <Loader /> : items.length ? (
        <div className="grid md:grid-cols-2 gap-5">
          {items.map((k) => (
            <div key={k.id} className="glass rounded-2xl p-6" data-testid={`kyc-card-${k.id}`}>
              <div className="flex items-center justify-between mb-4">
                <div><p className="font-heading text-white">{k.full_name}</p><p className="text-xs text-slate-500">{k.user_email}</p></div>
                <StatusBadge status={k.status} />
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                <div><p className="text-xs text-slate-500">DOB</p><p className="text-slate-300">{k.date_of_birth}</p></div>
                <div><p className="text-xs text-slate-500">ID Type</p><p className="text-slate-300 capitalize">{(k.id_type || "").replace("_", " ")}</p></div>
                <div><p className="text-xs text-slate-500">ID Number</p><p className="text-slate-300 font-mono">{k.id_number}</p></div>
                <div><p className="text-xs text-slate-500">Submitted</p><p className="text-slate-300">{fmtDate(k.created_at)}</p></div>
                <div className="col-span-2"><p className="text-xs text-slate-500">Address</p><p className="text-slate-300">{k.address}</p></div>
              </div>
              <div className="flex gap-4 mb-4">
                <ImageViewer src={k.document_url} testId={`kyc-doc-${k.id}`} label="View Document" />
                <ImageViewer src={k.selfie_url} testId={`kyc-selfie-${k.id}`} label="View Selfie" />
              </div>
              {k.rejection_reason && <p className="text-xs text-red-400 mb-3">Reason: {k.rejection_reason}</p>}
              {k.status === "pending" && (
                <div className="flex gap-2">
                  <ApproveButton testId={`kyc-approve-${k.id}`} onConfirm={() => approve(k.id)} />
                  <RejectButton testId={`kyc-reject-${k.id}`} onConfirm={(r) => reject(k.id, r)} />
                </div>
              )}
            </div>
          ))}
        </div>
      ) : <EmptyState title="No KYC submissions" subtitle={`No ${status} submissions found.`} />}
    </div>
  );
}
